import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import LinearProgress from "@mui/material/LinearProgress";

import useMutation from "src/hooks/useMutation";
import {
  UPDATE_INTERNAL_WORK_ORDER_ITEM_MUTATION,
  UPDATE_INTERNAL_WORK_ORDER_MUTATION,
  CREATE_INVENTORY_TRANSACTION_MUTATION,
} from "src/constants/graphql/mutations";
import StorageLocationAutocomplete from "src/views/capture/RackLocations/StorageLocationAutocomplete/StorageLocationAutocomplete";
import AssetImage from "src/components/wizard/Item/AssetImage";
import useNotification from "src/hooks/notification";

const getModeForItem = item => (item.pickedStatus ? "return" : "staging");

export default function IWOBulkStagingOrReturnModal({
  items,
  projectId,
  internalWorkOrder,
  onClose,
  onSuccess,
}) {
  const [saving, setSaving] = useState(false);
  const [location, setLocation] = useState({ warehouse: null, warehouse_locations: [], sub_qty: 0 });
  const { notifySuccess, notifyError } = useNotification();

  const [updateItem] = useMutation(UPDATE_INTERNAL_WORK_ORDER_ITEM_MUTATION);
  const [updateInternalWorkOrder] = useMutation(UPDATE_INTERNAL_WORK_ORDER_MUTATION);
  const [createTransaction] = useMutation(CREATE_INVENTORY_TRANSACTION_MUTATION);

  const isLocationValid = !!(location?.warehouse?.id && location?.warehouse_locations?.[0]?.id);
  const hasNonAllocatingItems = items.some(item => item.nonAllocating);

  const handleSubmit = async () => {
    if (hasNonAllocatingItems) {
      notifyError(
        "Non-allocating items must be processed individually so you can choose the current source location."
      );
      return;
    }

    setSaving(true);
    try {
      for (const item of items) {
        const mode = getModeForItem(item);
        const statusPatch = mode === "staging" ? { pickedStatus: true } : { completedStatus: true };
        const oldRackLocations = item.rackLocations || [];
        const destination = { ...location, sub_qty: item.quantity || 0 };
        const rackLocationsChanges = [destination];

        let rackLocations = oldRackLocations.map(l => ({ ...l }));
        if (rackLocations.length) {
          rackLocations[0] = { ...rackLocations[0], ...destination };
        } else {
          rackLocations.push(destination);
        }

        await createTransaction({
          variables: {
            code: item.asset.assetId,
            quantity: 0,
            notes: "",
            senderName: "",
            carrierName: "",
            trackingNumber: "",
            orderNumber: "",
            poNumber: "",
            rackLocations,
            rackLocationsChanges,
            oldRackLocations,
            internalWorkOrderItemId: item.id,
            actionSource:
              mode === "staging"
                ? "internal_work_order_staging"
                : "internal_work_order_return",
          },
        });

        await updateItem({ variables: { id: item.id, input: statusPatch } });
      }

      const stagedItemIds = new Set(
        items.filter(item => getModeForItem(item) === "staging").map(item => item.id)
      );
      const completedItemIds = new Set(
        items.filter(item => getModeForItem(item) === "return").map(item => item.id)
      );

      const allItemsStaged = internalWorkOrder?.internalWorkOrderItems?.every(item =>
        stagedItemIds.has(item.id) ? true : item.pickedStatus
      );
      const allItemsCompleted = internalWorkOrder?.internalWorkOrderItems?.every(item =>
        completedItemIds.has(item.id) ? true : item.completedStatus
      );

      if (internalWorkOrder?.id && (allItemsStaged || allItemsCompleted)) {
        const vaStatus = {};

        if (allItemsStaged) {
          vaStatus.picked = true;
        }

        if (allItemsCompleted) {
          vaStatus.completed = true;
        }

        await updateInternalWorkOrder({
          variables: {
            id: internalWorkOrder.id,
            input: { vaStatus },
          },
        });
      }

      notifySuccess(`Successfully processed ${items.length} item(s)`);
      onSuccess && onSuccess();
      onClose && onClose();
    } catch {
      notifyError("Failed to process items");
    } finally {
      setSaving(false);
    }
  };

  const stagingCount = items.filter(i => getModeForItem(i) === "staging").length;
  const returnCount = items.filter(i => getModeForItem(i) === "return").length;

  const summaryText = [
    stagingCount > 0 && `${stagingCount} to stage`,
    returnCount > 0 && `${returnCount} to return`,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Dialog open onClose={onClose} maxWidth={false} sx={{ "& .MuiDialog-paper": { width: 400 } }}>
      <DialogTitle>
        <Typography align="center" fontSize={18} fontWeight={500} color="text.primary">
          Internal Work Order Assistant
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography color="text.primary" fontSize={15} mb={1} align="center">
          Where are you{stagingCount > 0 ? " staging" : ""}{stagingCount > 0 && returnCount > 0 ? "/" : ""}{returnCount > 0 ? " returning" : ""} these items?
        </Typography>
        <StorageLocationAutocomplete
          name="location"
          value={location}
          projectId={projectId}
          onChange={values => setLocation(prev => ({ ...prev, ...values }))}
          disabled={saving}
          loading={false}
        />
        <Typography variant="body2" color="text.secondary" mt={2} mb={1} align="center">
          {items.length} item(s) found — {summaryText}:
        </Typography>
        {hasNonAllocatingItems && (
          <Typography color="error" variant="body2" mb={1} align="center">
            Non-allocating items must be processed individually so the current source location can be selected.
          </Typography>
        )}
        <List dense disablePadding>
          {items.map((item, i) => {
            const images = item.asset?.images || [];
            const imageUrl = (images.find(img => img.default) || images[0])?.url;
            return (
              <React.Fragment key={item.id}>
                {i > 0 && <Divider />}
                <ListItem disableGutters sx={{ py: 1 }}>
                  <Box mr={1.5} flexShrink={0}>
                    <AssetImage width={60} height={50} image={imageUrl} />
                  </Box>
                  <ListItemText
                    primary={item.asset?.itemId}
                    secondary={item.asset?.description}
                  />
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>
        {saving && <LinearProgress sx={{ mt: 2 }} />}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isLocationValid || saving || hasNonAllocatingItems}
        >
          Confirm All
        </Button>
      </DialogActions>
    </Dialog>
  );
}
